import type { CSSProperties } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { Icon } from '../components/Icon'
import { modulesForCourse } from '../data/catalog'
import { COURSES } from '../data/courses'
import { useProgress } from '../state/ProgressContext'

export function CoursePage() {
  const { courseId } = useParams()
  const course = COURSES.find((item) => item.id === courseId)
  const { completedForModule, progress } = useProgress()
  if (!course) return <Navigate to="/" replace />
  const modules = modulesForCourse(course.id)
  const sectionTotal = modules.reduce((sum, module) => sum + module.sections.length, 0)
  const sectionComplete = modules.reduce((sum, module) => sum + completedForModule(module.id), 0)
  const percent = sectionTotal ? Math.round((sectionComplete / sectionTotal) * 100) : 0
  const current = progress.lastPosition && modules.some((module) => module.id === progress.lastPosition?.moduleId)
    ? progress.lastPosition
    : null
  const firstTarget = current ? `/modules/${current.moduleId}#${current.sectionId}` : `/modules/${modules[0].id}`
  return (
    <div className="page-width page-stack">
      <nav className="breadcrumbs" aria-label="Breadcrumb"><Link to="/">Home</Link><Icon name="chevron" size={14} /><span>Course {course.number}</span></nav>
      <section className="course-hero" style={{ '--course-color': course.color, '--course-soft': course.softColor } as CSSProperties}>
        <div className="course-number-large">Course {course.number}</div>
        <div>
          <span className="eyebrow">{course.eyebrow}</span>
          <h1>{course.title}</h1>
          <p>{course.description}</p>
          <div className="hero-actions">
            <Link className="button primary" to={firstTarget}>{current ? 'Continue this course' : 'Start this course'}<Icon name="chevron" size={17} /></Link>
            <Link className="button" to={`/search?q=${encodeURIComponent(course.title)}`}><Icon name="search" size={17} />Search this topic</Link>
          </div>
        </div>
        <div className="course-progress-card">
          <strong>{percent}%</strong><span>complete</span>
          <div><i style={{ width: `${percent}%` }} /></div>
          <small>{sectionComplete} of {sectionTotal} sections</small>
        </div>
      </section>
      <section className="module-list-section">
        <div className="section-heading compact">
          <div><span className="eyebrow">Clear learning order</span><h2>{modules.length} modules</h2></div>
          <p>{course.preview}</p>
        </div>
        <div className="module-list">
          {modules.map((module, index) => {
            const complete = completedForModule(module.id)
            const percentComplete = Math.round((complete / module.sections.length) * 100)
            return (
              <Link className="module-row" to={`/modules/${module.id}`} key={module.id}>
                <span className="module-order">{String(index + 1).padStart(2, '0')}</span>
                <span className="module-row-copy">
                  <span><b>{module.id}</b>{percentComplete === 100 ? <em><Icon name="check" size={13} />Complete</em> : null}</span>
                  <strong>{module.title}</strong>
                  <p>{module.summary}</p>
                  <small>{module.sections.length} sections · {complete} completed</small>
                </span>
                <span className="row-progress"><i style={{ width: `${percentComplete}%` }} /></span>
                <Icon name="chevron" />
              </Link>
            )
          })}
        </div>
      </section>
      <aside className="financial-disclaimer"><strong>Learn, compare, then decide with context.</strong><span> The course explains competing approaches and their trade-offs. It does not replace individualized professional advice.</span></aside>
    </div>
  )
}
